import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export interface NotificationPreferences {
  event_reminders: boolean;
  social_activity: boolean;
  event_chat: boolean;
  messages: boolean;
  announcements: boolean;
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  event_reminders: true,
  social_activity: true,
  event_chat: true,
  messages: true,
  announcements: true,
};

export function useNotificationPreferences() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['notification-preferences', user?.id],
    queryFn: async () => {
      if (!user?.id) return DEFAULT_PREFERENCES;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('notification_preferences')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching notification preferences:', error);
        return DEFAULT_PREFERENCES;
      }
      
      const prefs = data?.notification_preferences as unknown as NotificationPreferences | null;
      return prefs || DEFAULT_PREFERENCES;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const updatePreferenceMutation = useMutation({
    mutationFn: async ({ key, value }: { key: keyof NotificationPreferences; value: boolean }) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const currentPrefs = preferences || DEFAULT_PREFERENCES;
      const updatedPrefs = { ...currentPrefs, [key]: value };
      
      const { error } = await supabase
        .from('profiles')
        .update({ notification_preferences: updatedPrefs })
        .eq('id', user.id);
      
      if (error) throw error;
      return updatedPrefs;
    },
    onSuccess: (newPrefs) => {
      queryClient.setQueryData(['notification-preferences', user?.id], newPrefs);
      toast.success('Preference updated');
    },
    onError: (error) => {
      console.error('Error updating notification preference:', error);
      toast.error('Failed to update preference');
    },
  });

  const updatePreference = (key: keyof NotificationPreferences, value: boolean) => {
    updatePreferenceMutation.mutate({ key, value });
  };

  return {
    preferences: preferences || DEFAULT_PREFERENCES,
    isLoading,
    updatePreference,
    isUpdating: updatePreferenceMutation.isPending,
  };
}

export function useNotificationIntroToast() {
  const { user } = useAuth();
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    const checkAndShowIntro = async () => {
      if (!user?.id || hasShown) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('notification_preferences_intro_shown')
        .eq('id', user.id)
        .single();

      if (error || data?.notification_preferences_intro_shown) return;

      // Show the intro toast
      toast.info("You're all set for notifications!", {
        description: "Adjust your preferences anytime in Settings → Notifications",
        duration: 6000,
      });

      // Mark as shown
      await supabase
        .from('profiles')
        .update({ notification_preferences_intro_shown: true })
        .eq('id', user.id);

      setHasShown(true);
    };

    // Delay slightly to not interfere with initial load
    const timer = setTimeout(checkAndShowIntro, 3000);
    return () => clearTimeout(timer);
  }, [user?.id, hasShown]);
}
